import { useNavigate } from 'react-router-dom'
import { FC } from 'react'
import { List, Stack, Typography } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'

import {
  SidePanelHeader,
  SidePanelObject,
  SidePanelElementProps,
  useSidePanelContext,
} from '@clara/layout-next'
import { Translation, useT } from '@clara/locale'
import { DebitIcon, SafetyIcon, UnavailableInvoiceIcon } from '@clara/icons'
import { AsyncState } from '@clara/hooks'

import { SettingsOption } from '../common/settings-option'
import { getBankInfoSidePanel } from '../bank-info/sidepanel'
import { BankInfoResponse, UserInfoResponse } from '../../api/bank-info'
import { getDisableFunctionalitySidePanel } from './disable-functionality'

type Props = {
  getBankInfo: () => Promise<BankInfoResponse>
  bankInfo?: BankInfoResponse
  isCompanyOwner: boolean
  userInfoResponse?: AsyncState<UserInfoResponse>
}

const SettingsSidePanel: FC<SidePanelElementProps & Props> = ({
  getBankInfo,
  bankInfo,
  isCompanyOwner,
  userInfoResponse,
}) => {
  const { t } = useT('reimbursements')
  const navigate = useNavigate()
  const sidePanel = useSidePanelContext()
  const { reimbursements_settings_temp_disable } = useFlags()

  return (
    <Stack>
      <Typography
        variant="body1"
        sx={(theme) => ({
          color: theme.palette.v3.content.medium_emphasis,
          marginBottom: theme.spacing(3),
        })}
      >
        {t('settings.description')}
      </Typography>
      <List>
        <SettingsOption
          title={t('bankinfo.sidepanel.title')}
          description={t('settings.bankinfo.description')}
          icon={<DebitIcon sx={{ width: '24px', height: '24px' }} />}
          onClick={() => {
            sidePanel.onOpen(
              sidePanel.panels.concat(
                getBankInfoSidePanel({
                  getBankInfo,
                  bankInfo,
                  userInfoResponse,
                }),
              ),
              sidePanel.index + 1,
            )
          }}
        />

        <SettingsOption
          title={t('settings.approvalflow.title')}
          description={t('settings.approvalflow.description')}
          icon={<SafetyIcon sx={{ width: '24px', height: '24px' }} />}
          onClick={() => {
            navigate('/approvals-flow/reimbursement')
          }}
        />
        {(isCompanyOwner && !reimbursements_settings_temp_disable) ?? (
          <SettingsOption
            title={t('settings.disablefunctionality.title')}
            description={t('settings.disablefunctionality.description')}
            icon={
              <UnavailableInvoiceIcon sx={{ width: '24px', height: '24px' }} />
            }
            onClick={() => {
              sidePanel.onOpen(
                sidePanel.panels.concat(getDisableFunctionalitySidePanel()),
                sidePanel.index + 1,
              )
            }}
          />
        )}
      </List>
    </Stack>
  )
}

export const getSettingsSidePanel = ({
  getBankInfo,
  bankInfo,
  isCompanyOwner,
  userInfoResponse,
}: Props) =>
  [
    {
      children: [
        {
          element: (
            <SettingsSidePanel
              getBankInfo={getBankInfo}
              bankInfo={bankInfo}
              isCompanyOwner={isCompanyOwner}
              userInfoResponse={userInfoResponse}
            />
          ),
          header: (
            <Translation ns="reimbursements">
              {(t) => <SidePanelHeader headline={t('settings.title')} />}
            </Translation>
          ),
        },
      ],
    },
  ] as SidePanelObject[]
